import { useMemo } from "react";
import type { ISection } from "src/interfaces";

interface UseCumulativeSectionsOptions {
  featureKey: string;
  destId: string;
}

export function useCumulativeFeature(
  sections: ISection[],
  { featureKey, destId }: UseCumulativeSectionsOptions,
): ISection[] {
  return useMemo(() => {
    if (!Array.isArray(sections)) return [];

    const sourceIdx = sections.findIndex(sec =>
      sec.headers?.some(
        h =>
          Array.isArray(h.cols) && h.cols.some(c => c.feature === featureKey),
      ),
    );
    let cumulative = 0;
    if (sourceIdx !== -1) {
      sections[sourceIdx].rows.forEach((row: any) => {
        const cols = row.cols || {};
        Object.values(cols).forEach((cell: any) => {
          if (cell.feature === featureKey) {
            const n = parseFloat(cell.value);
            if (!isNaN(n)) cumulative += n;
          }
        });
      });
    }

    const destIdx = sections.findIndex(sec =>
      sec.headers?.some(
        h =>
          h.id === destId ||
          (Array.isArray(h.cols) && h.cols.some(c => c.id === destId)),
      ),
    );
    if (destIdx === -1) {
      return sections;
    }

    const row0 = sections[destIdx].rows?.[0] as any;
    const cols0 = row0?.cols || {};
    const destKey = Object.keys(cols0).find(k => cols0[k].id === destId);
    if (!destKey) return sections;

    return sections.map((sec, idx) => {
      if (idx !== destIdx) return sec;
      const originalRow = sec.rows[0] as any;
      return {
        ...sec,
        rows: [
          {
            ...originalRow,
            cols: {
              ...originalRow.cols,
              [destKey]: {
                ...originalRow.cols[destKey],
                value: cumulative.toFixed(2),
              },
            },
          },
        ],
      };
    });
  }, [sections, featureKey, destId]);
}
